import { ChatMessageItem } from '../../types/topic';

/**
 * Input structure for creating a chat message
 */
export interface CreateChatMessageInput {
  topicId: string;
  content: string;
  role: 'user' | 'assistant' | 'system';
  userId?: string | null;
  summary?: string | null;
  metadata?: Record<string, any>;
}

/**
 * Input structure for updating a chat message
 */
export interface UpdateChatMessageInput {
  content?: string;
  highlights?: string[];
  summary?: string | null;
  metadata?: Record<string, any>;
}

/**
 * Raw chat message data as returned from the database
 */
export interface ChatMessageData {
  id: string;
  topic_id: string;
  user_id?: string | null;
  content: string;
  role: 'user' | 'assistant' | 'system';
  created_at: string;
  updated_at: string;
  highlights?: string[];
  summary?: string | null;
  metadata?: Record<string, any>;
  user_display_name?: string;
  user_avatar_url?: string;
}

/**
 * Formatted chat message as used in the application
 */
export interface FormattedChatMessage {
  id: string;
  topicId: string;
  userId?: string | null;
  content: string;
  role: 'user' | 'assistant' | 'system';
  createdAt: string;
  updatedAt: string;
  highlights: string[];
  summary: string | null;
  metadata?: Record<string, any>;
  userMetadata: {
    displayName: string;
    avatarUrl?: string | null;
  };
}

/**
 * Helper function to format chat message data from the database
 * into a consistent structure for the app
 */
export function formatChatMessage(message: ChatMessageData): FormattedChatMessage {
  return {
    id: message.id,
    topicId: message.topic_id,
    userId: message.user_id || null,
    content: message.content,
    role: message.role,
    createdAt: message.created_at,
    updatedAt: message.updated_at,
    highlights: message.highlights || [],
    summary: message.summary || null,
    metadata: message.metadata || {},
    userMetadata: {
      displayName: message.user_display_name || 'User',
      avatarUrl: message.user_avatar_url || null,
    },
  };
}

/**
 * Converts a FormattedChatMessage back to the database structure
 * Useful for optimistic updates and offline syncing
 */
export function convertToRawMessage(message: FormattedChatMessage): ChatMessageData {
  return {
    id: message.id,
    topic_id: message.topicId,
    user_id: message.userId,
    content: message.content,
    role: message.role,
    created_at: message.createdAt,
    updated_at: message.updatedAt,
    highlights: message.highlights,
    summary: message.summary,
    metadata: message.metadata,
    user_display_name: message.userMetadata.displayName,
    user_avatar_url: message.userMetadata.avatarUrl || undefined,
  };
}

/**
 * Convert from legacy ChatMessageItem to FormattedChatMessage
 * Used for backward compatibility during migration
 */
export function convertFromLegacyMessage(message: ChatMessageItem): FormattedChatMessage {
  return {
    id: message.id,
    topicId: message.topic_id,
    userId: message.user_id || null,
    content: message.content,
    role: message.role,
    createdAt: message.created_at,
    updatedAt: message.updated_at,
    highlights: message.highlights || [],
    summary: message.summary || null,
    metadata: message.metadata || {},
    userMetadata: {
      displayName: message.user_metadata?.display_name || 'User',
      avatarUrl: message.user_metadata?.avatar_url || null,
    },
  };
}